<script>
import useValidation from "@/use/Validation";
import useSharedData from "@/use/SharedData";
import useSharedMethod from "@/use/SharedMethod";
import useTools from "@/use/Tools";
import {useMedicalReasonStore} from "@/stores/Medical/MedicalReason";
import {computed, onMounted, ref} from "vue";
import axios from "axios";
import useSharedSelectData from "@/use/SharedSelectData";

export default {
    name: "MedicalReasonForm",
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        viewAction: Boolean,
        deleteAction: Boolean,
    },
    setup(props) {
        const readonly = computed(() => props.viewAction || props.deleteAction);
        const {mxRules} = useValidation();
        const {dialog, loading, overlay} = useSharedData();
        const {periodicity} = useSharedSelectData();
        const {getBack} = useSharedMethod();
        const {$refs, $route} = useTools();

        const medicalReasonStore = useMedicalReasonStore();

        const medicalReason = ref({enablePeriodicity: false});

        function findById(id) {
            loading.value = true;
            axios.get(`/medicalReasons/${id}`).then((response) => {
                medicalReason.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }


        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/medicalReasons', medicalReason.value).then((response) => {
                    medicalReasonStore.addMedicalReason(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/medicalReasons', medicalReason.value).then((response) => {
                    medicalReasonStore.updateMedicalReason(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        function remove() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.delete(`/medicalReasons/${medicalReason.value.id}`).then(() => {
                    medicalReasonStore.deleteMedicalReason(medicalReason.value);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }

        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });

        return {
            medicalReasonStore,
            mxRules,
            readonly,
            dialog,
            loading,
            overlay,
            periodicity,
            medicalReason,
            save,
            update,
            remove,
            getBack,
        };
    }
}
</script>

<template>
    <v-dialog
        v-model="dialog"
        max-width="700"
        persistent
        scrollable
    >
        <v-card>
            <w-dialog-toolbar
                :view-action="viewAction"
                :delete-action="deleteAction"
                :add-action="addAction"
                :update-action="updateAction"
            />
            <w-overlay
                :overlay="overlay"
                :loading="loading"
                :delete-action="deleteAction"
                @remove="remove"
                @cancel="overlay = false"
            />
            <v-card-text>
                <v-container grid-list-md>
                    <v-form ref="form">
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-text-field
                                        v-model="medicalReason.code"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.label')" required/>
                                    <w-text-field
                                        v-model="medicalReason.name"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.duration')" required/>
                                    <w-text-field
                                        v-model="medicalReason.duration"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                        :suffix="$t('common.DAILY')"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <v-checkbox
                                        v-model="medicalReason.enablePeriodicity"
                                        :label="$t('common.periodicity')"
                                        hide-details
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-text-field
                                        v-model="medicalReason.periodicityDuration"
                                        :disabled="!medicalReason.enablePeriodicity"
                                        :readonly="readonly"
                                        :rules="medicalReason.enablePeriodicity ? [mxRules.required]: []"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-enum-select
                                        v-model="medicalReason.periodicity"
                                        :items="periodicity"
                                        :disabled="!medicalReason.enablePeriodicity"
                                        :readonly="readonly"
                                        :rules="medicalReason.enablePeriodicity ? [mxRules.required]: []"
                                    />
                                </v-col>

                                <v-col cols="12">
                                    <w-label :label="$t('common.comment')"/>
                                    <w-simple-text-area
                                        v-model="medicalReason.observation"
                                        :readonly="readonly"
                                    />
                                </v-col>

                            </v-row>
                        </w-fieldset>
                    </v-form>
                </v-container>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save(false)"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>